import { createApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { ZodType } from 'zod/v4';
import { env } from '@/env';
import type { AppDispatch, RootState } from '@/store/store';
import { baseQueryWithReauth, doRefresh } from './baseQueryWithReauth';
import { UnauthorizedSseError } from './sseAuthErrors';
import { pitchDeckListItemSchema } from './__generated__/zod/pitchDeckListItemSchema';
import { pitchDeckDetailDtoSchema } from './__generated__/zod/pitchDeckDetailDtoSchema';
import { dealflowRowDtoSchema } from './__generated__/zod/dealflowRowDtoSchema';
import type { UserListItem } from './__generated__/types/UserListItem';
import type { InviteListItem } from './__generated__/types/InviteListItem';
import { listUsers200Schema } from './__generated__/zod/adminController/listUsersSchema';
import { listInvites200Schema } from './__generated__/zod/adminController/listInvitesSchema';
import type { UserRole } from '@/types/roles';
import { z } from 'zod/v4';
import type { PitchDeckListItem } from './__generated__/types/PitchDeckListItem';

const dealflowListSchema = z.array(dealflowRowDtoSchema);
export type DealflowRow = z.infer<typeof dealflowRowDtoSchema>;
import type { PitchDeckDetailDto as PitchDeckDetail } from './__generated__/types/PitchDeckDetailDto';
import type { PitchDeckStatusDto } from './__generated__/types/PitchDeckStatusDto';
import type { PitchDeckReviewStateDto } from './__generated__/types/PitchDeckReviewStateDto';
import type { RatingDto } from './__generated__/types/RatingDto';
import type { RatingCategoryDto } from './__generated__/types/RatingCategoryDto';
import type { SubmitRatingRequestModeEnumKey } from './__generated__/types/SubmitRatingRequest';
import type { ReviewerDirectoryItemDto } from './__generated__/types/ReviewerDirectoryItemDto';
import type { AuthConfig } from './config/models/AuthConfig';
import type { ConstantsConfig, PitchDeckSubmissionDetails } from './config/models/ConstantsConfig';
import type { AuthResultPublic } from './auth/models/AuthResultPublic';
import type { MeDto } from './auth/models/MeDto';
import type { SubmitResult } from './publicUpload/models/SubmitResult';
import type { ProfileDto } from './__generated__/types/ProfileDto';
import type { UpdateProfileRequest } from './__generated__/types/UpdateProfileRequest';
import type { FundProfileDto } from './__generated__/types/FundProfileDto';
import type { UpdateFundProfileRequest } from './__generated__/types/UpdateFundProfileRequest';
import type { FundThesisDto } from './__generated__/types/FundThesisDto';
import type { UpdateFundThesisRequest } from './__generated__/types/UpdateFundThesisRequest';
import type { AuditLogItemDto } from './__generated__/types/AuditLogItemDto';
import type { SavedSearchViewDto } from './__generated__/types/SavedSearchViewDto';
import type { SaveSearchViewRequest } from './__generated__/types/SaveSearchViewRequest';

const pitchDeckListSchema = z.array(pitchDeckListItemSchema);

const isTerminalStage = (stage?: string | null) =>
  stage === 'TerminalSucceeded' || stage === 'TerminalRejected' || stage === 'TerminalFailed';

// Defer background work until the browser is idle so it never competes with
// initial render. Falls back to a macrotask where requestIdleCallback is absent.
function runWhenIdle(fn: () => void): void {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (typeof ric === 'function') ric(fn);
  else setTimeout(fn, 200);
}

// Throws a FetchBaseQueryError-shaped error so it surfaces in `error` from hooks
// the same way HTTP errors do.
function validate<T>(schema: ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const err: FetchBaseQueryError = {
      status: 'CUSTOM_ERROR',
      error: 'ApiContractError',
      data: result.error.issues,
    };
    throw err;
  }
  return result.data;
}

export const rysApi = createApi({
  reducerPath: 'rysApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PitchDeck', 'PitchDeckList', 'User', 'Invite', 'Profile', 'Me', 'ProfilePhoto', 'FundProfile', 'FundLogo', 'FundThesis', 'FundThesisDraft', 'AuditLog', 'SavedView', 'Dealflow'],
  endpoints: builder => ({
    // --- Dealflow ---------------------------------------------------------
    listDealflow: builder.query<DealflowRow[], void>({
      query: () => '/dealflow',
      transformResponse: (raw: unknown) => validate(dealflowListSchema, raw),
      providesTags: ['Dealflow'],
      keepUnusedDataFor: 60,
    }),

    verifyClarification: builder.mutation<void, string>({
      query: id => ({ url: `/pitchdecks/${id}/clarification/verify`, method: 'POST' }),
      invalidatesTags: ['Dealflow', 'PitchDeckList'],
    }),

    rejectClarification: builder.mutation<void, string>({
      query: id => ({ url: `/pitchdecks/${id}/clarification/reject`, method: 'POST' }),
      invalidatesTags: ['Dealflow'],
    }),

    // --- Pitch decks ------------------------------------------------------
    listPitchDecks: builder.query<PitchDeckListItem[], void>({
      query: () => '/pitchdecks',
      transformResponse: (raw: unknown) => validate(pitchDeckListSchema, raw),
      providesTags: ['PitchDeckList'],
      keepUnusedDataFor: 60,
    }),

    getPitchDeck: builder.query<PitchDeckDetail, string>({
      query: id => `/pitchdecks/${id}`,
      transformResponse: (raw: unknown) => validate(pitchDeckDetailDtoSchema, raw),
      providesTags: (_r, _e, id) => [{ type: 'PitchDeck', id }],
      // Deck detail rarely changes once viewed: a terminal deck is frozen, and an
      // in-flight one is kept live by the SSE stream while mounted (and re-patched
      // on remount via onCacheEntryAdded). Retain it long (20 min) so revisiting a
      // deck is instant with no refetch. (Slide-page blobs stay at 60s to avoid
      // pinning many full-res object URLs in memory.)
      keepUnusedDataFor: 1200,
      // Open an SSE stream tied to this cache entry. Each `status` event
      // patches the cached detail; the stream is torn down when the entry
      // is evicted (last subscriber unmounts + cacheTime expires).
      async onCacheEntryAdded(id, { cacheDataLoaded, cacheEntryRemoved, updateCachedData, getState, dispatch }) {
        let controller = new AbortController();
        let hasRetriedOnce = false;
        let cancelled = false;
        let detail: PitchDeckDetail;
        try {
          ({ data: detail } = await cacheDataLoaded);
        } catch {
          return; // initial request failed — nothing to stream against
        }

        // Warm ONLY the first couple of deck pages so opening the preview is
        // instant — deferred to idle so it never competes with initial render.
        // The rest load progressively as the user steps through the lightbox
        // (see DeckPreview's look-ahead). Preloading the whole deck up front
        // froze the UI (N full-res blob fetches at once); we deliberately don't.
        const pageCount = detail.upstreamStatus?.report?.slides?.data?.items?.length ?? 0;
        if (pageCount > 0) {
          runWhenIdle(() => {
            if (cancelled) return;
            prefetchDeckPages(dispatch as AppDispatch, id, 1, Math.min(2, pageCount));
          });
        }

        const openStream = (token: string) => {
          void fetchEventSource(`${env.api.baseUrl}/pitchdecks/${id}/events`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            async onopen(response) {
              if (response.status === 401) {
                throw new UnauthorizedSseError();
              }
              // any non-401 status: accept and proceed
            },
            onmessage(msg) {
              try {
                if (msg.event === 'status') {
                  const status = JSON.parse(msg.data) as PitchDeckStatusDto;
                  updateCachedData(draft => {
                    draft.upstreamStatus = status;
                  });
                  // On a terminal processing stage, refresh ONLY the dashboard
                  // list — never the PitchDeck-id detail tag, which would tear
                  // down this live SSE stream.
                  if (isTerminalStage(status.processingStage)) {
                    dispatch(rysApi.util.invalidateTags(['PitchDeckList']));
                  }
                } else if (msg.event === 'snapshot') {
                  const snap = JSON.parse(msg.data) as PitchDeckDetail;
                  updateCachedData(() => snap);
                  // Same terminal-stage list refresh; detail tag is left intact
                  // to preserve the SSE stream.
                  if (isTerminalStage(snap.upstreamStatus?.processingStage)) {
                    dispatch(rysApi.util.invalidateTags(['PitchDeckList']));
                  }
                }
              } catch {
                // malformed payload — ignore
              }
            },
            onerror(err) {
              if (err instanceof UnauthorizedSseError && !hasRetriedOnce) {
                hasRetriedOnce = true;
                controller.abort();
                // Fire-and-forget: refresh, then reopen the stream with the
                // new bearer. Throwing below stops fetch-event-source's own
                // auto-retry; this async work runs independently.
                void (async () => {
                  const refreshed = await doRefresh(dispatch as AppDispatch);
                  if (!refreshed) return;
                  const newToken = (getState() as RootState).auth.accessToken;
                  if (!newToken) return;
                  if (cancelled) return;
                  controller = new AbortController();
                  openStream(newToken);
                })();
                throw err;
              }
              // Non-401 error or already retried: abort to stop auto-retry.
              controller.abort();
              throw err;
            },
          });
        };

        const initialToken = (getState() as RootState).auth.accessToken;
        if (!initialToken) return;
        openStream(initialToken);

        await cacheEntryRemoved;
        cancelled = true;
        controller.abort();
      },
    }),

    getPitchDeckPage: builder.query<string, { deckId: string; pageNumber: number }>({
      query: ({ deckId, pageNumber }) => ({
        url: `/pitchdecks/${deckId}/pages/${pageNumber}`,
        responseHandler: response => response.blob().then(b => URL.createObjectURL(b)),
        cache: 'no-cache',
      }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved }) {
        try {
          const entry = await cacheDataLoaded;
          await cacheEntryRemoved;
          URL.revokeObjectURL(entry.data);
        } catch {
          // never resolved
        }
      },
    }),

    getAttachmentBlob: builder.query<string, { deckId: string; attachmentId: string }>({
      query: ({ deckId, attachmentId }) => ({
        url: `/pitchdecks/${deckId}/attachments/${attachmentId}`,
        responseHandler: response => response.blob().then(b => URL.createObjectURL(b)),
        cache: 'no-cache',
      }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved }) {
        try {
          const entry = await cacheDataLoaded;
          await cacheEntryRemoved;
          URL.revokeObjectURL(entry.data);
        } catch {
          // never resolved
        }
      },
    }),

    setReviewDecision: builder.mutation<PitchDeckReviewStateDto, { id: string; decision: string }>({
      query: ({ id, decision }) => ({ url: `/pitchdecks/${id}/decision`, method: 'POST', body: { decision } }),
      // Refresh the dashboard list only (decision is list-visible). The detail
      // cache keeps its live SSE stream because the PitchDeck-id tag is untouched.
      invalidatesTags: ['PitchDeckList'],
      // Patch the cached detail directly instead of invalidating: the getPitchDeck
      // cache entry holds a live SSE stream, and a full refetch would tear it down
      // and reconnect on every click.
      async onQueryStarted({ id, decision }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            rysApi.util.updateQueryData('getPitchDeck', id, draft => {
              if (draft) {
                draft.reviewDecision = data.reviewDecision;
                draft.isWatchlisted = data.isWatchlisted;
                // Optimistically flip the current user's own avatar in the
                // "Rated by" reviewers list so it updates without waiting for a
                // getDeckReviewers refetch. The authoritative list arrives via
                // the next fetch / SSE snapshot.
                if (draft.reviewers) {
                  const mine = draft.reviewers.find(r => r.isCurrentUser);
                  if (mine) {
                    mine.decision = decision === 'Accepted' ? 'Accepted' : decision === 'Rejected' ? 'Rejected' : 'Pending';
                  }
                }
              }
            }),
          );
        } catch {
          /* on failure, leave cache as-is; UI reflects last-known state */
        }
      },
    }),

    setWatchlist: builder.mutation<PitchDeckReviewStateDto, { id: string; watchlisted: boolean }>({
      query: ({ id, watchlisted }) => ({ url: `/pitchdecks/${id}/watchlist`, method: 'POST', body: { watchlisted } }),
      // Refresh the dashboard list only (watchlist is list-visible). The detail
      // cache keeps its live SSE stream because the PitchDeck-id tag is untouched.
      invalidatesTags: ['PitchDeckList'],
      // Patch the cached detail directly instead of invalidating: the getPitchDeck
      // cache entry holds a live SSE stream, and a full refetch would tear it down
      // and reconnect on every click.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            rysApi.util.updateQueryData('getPitchDeck', id, draft => {
              if (draft) {
                draft.reviewDecision = data.reviewDecision;
                draft.isWatchlisted = data.isWatchlisted;
              }
            }),
          );
        } catch {
          /* on failure, leave cache as-is; UI reflects last-known state */
        }
      },
    }),

    submitRating: builder.mutation<
      RatingDto,
      { id: string; mode: SubmitRatingRequestModeEnumKey; score?: number | null; comment?: string; categories?: RatingCategoryDto[] }
    >({
      query: ({ id, mode, score, comment, categories }) => ({
        url: `/pitchdecks/${id}/rating`,
        method: 'PUT',
        body: { mode, score, comment, categories },
      }),
      // Patch the cached detail directly instead of invalidating: the getPitchDeck
      // cache entry holds a live SSE stream, and a full refetch would tear it down.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            rysApi.util.updateQueryData('getPitchDeck', id, draft => {
              if (!draft) return;
              const list = draft.ratings ?? (draft.ratings = []);
              const idx = list.findIndex(r => r.isCurrentUser);
              if (idx >= 0) list[idx] = data;
              else list.push(data);
            }),
          );
        } catch {
          /* on failure, leave cache as-is */
        }
      },
    }),

    removeRating: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/pitchdecks/${id}/rating`, method: 'DELETE' }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            rysApi.util.updateQueryData('getPitchDeck', id, draft => {
              if (draft?.ratings) draft.ratings = draft.ratings.filter(r => !r.isCurrentUser);
            }),
          );
        } catch {
          /* on failure, leave cache as-is */
        }
      },
    }),

    // --- Reviewers --------------------------------------------------------
    listReviewers: builder.query<ReviewerDirectoryItemDto[], void>({
      query: () => `/reviewers`,
    }),

    // --- Auth -------------------------------------------------------------
    requestEmailCode: builder.mutation<void, { email: string; turnstileToken: string | null }>({
      query: body => ({ url: '/auth/request-code', method: 'POST', body }),
    }),

    verifyEmailCode: builder.mutation<AuthResultPublic, { email: string; code: string; turnstileToken?: string | null }>({
      query: body => ({ url: '/auth/verify-code', method: 'POST', body }),
    }),

    logout: builder.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
    }),

    getMe: builder.query<MeDto, void>({
      query: () => '/me',
      providesTags: ['Me'],
    }),

    getProfile: builder.query<ProfileDto, void>({
      query: () => '/me/profile',
      providesTags: ['Profile'],
    }),

    updateProfile: builder.mutation<ProfileDto, UpdateProfileRequest>({
      query: body => ({ url: '/me/profile', method: 'PUT', body }),
      invalidatesTags: ['Profile', 'Me'],
    }),

    uploadProfilePhoto: builder.mutation<ProfileDto, { file: File }>({
      query: ({ file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: '/me/profile/photo', method: 'POST', body: form };
      },
      invalidatesTags: ['Profile', 'Me', 'ProfilePhoto'],
    }),

    removeProfilePhoto: builder.mutation<ProfileDto, void>({
      query: () => ({ url: '/me/profile/photo', method: 'DELETE' }),
      invalidatesTags: ['Profile', 'Me', 'ProfilePhoto'],
    }),

    // The photo endpoint streams raw bytes behind bearer auth, so a plain
    // <img src> can't load it. Fetch it as a blob through the authenticated
    // base query and hand the component an object URL to render.
    getProfilePhoto: builder.query<string, void>({
      query: () => ({ url: '/me/profile/photo', responseHandler: response => response.blob() }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
      providesTags: ['ProfilePhoto'],
    }),

    // --- Fund profile -----------------------------------------------------
    getFundProfile: builder.query<FundProfileDto, void>({
      query: () => '/fund/profile',
      providesTags: ['FundProfile'],
    }),

    updateFundProfile: builder.mutation<FundProfileDto, UpdateFundProfileRequest>({
      query: body => ({ url: '/fund/profile', method: 'PUT', body }),
      invalidatesTags: ['FundProfile'],
    }),

    uploadFundLogo: builder.mutation<FundProfileDto, { file: File }>({
      query: ({ file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: '/fund/profile/logo', method: 'POST', body: form };
      },
      invalidatesTags: ['FundProfile', 'FundLogo'],
    }),

    removeFundLogo: builder.mutation<FundProfileDto, void>({
      query: () => ({ url: '/fund/profile/logo', method: 'DELETE' }),
      invalidatesTags: ['FundProfile', 'FundLogo'],
    }),

    // Logo streams raw bytes behind bearer auth — fetch as a blob and hand the
    // component an object URL (a plain <img src> can't carry the token).
    getFundLogo: builder.query<string, void>({
      query: () => ({ url: '/fund/profile/logo', responseHandler: response => response.blob() }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
      providesTags: ['FundLogo'],
    }),

    // --- Fund thesis ------------------------------------------------------
    getFundThesis: builder.query<FundThesisDto, void>({
      query: () => '/fund/thesis',
      providesTags: ['FundThesis'],
    }),

    // Promote: the client sends the full thesis it assembled (live + its edits);
    // the server validates + writes the live row (last-write-wins) and clears the draft.
    updateFundThesis: builder.mutation<FundThesisDto, UpdateFundThesisRequest>({
      query: body => ({ url: '/fund/thesis', method: 'PUT', body }),
      invalidatesTags: ['FundThesis', 'FundThesisDraft'],
    }),

    getFundThesisDraft: builder.query<FundThesisDto | null, void>({
      query: () => '/fund/thesis/draft',
      // 204 No Content (no draft) → RTK gives an empty/undefined body; normalize to null.
      transformResponse: (res: FundThesisDto | null | undefined) => res ?? null,
      providesTags: ['FundThesisDraft'],
    }),
    // Autosave: upsert the whole-thesis draft snapshot. Deliberately does NOT invalidate
    // 'FundThesisDraft' — re-fetching the draft on every autosave would fight the editor's
    // local state.
    saveFundThesisDraft: builder.mutation<void, UpdateFundThesisRequest>({
      query: body => ({ url: '/fund/thesis/draft', method: 'PUT', body }),
    }),
    discardFundThesisDraft: builder.mutation<void, void>({
      query: () => ({ url: '/fund/thesis/draft', method: 'DELETE' }),
      invalidatesTags: ['FundThesisDraft'],
    }),

    // --- Saved search views -----------------------------------------------
    listSavedViews: builder.query<SavedSearchViewDto[], void>({
      query: () => '/saved-views',
      providesTags: ['SavedView'],
    }),
    saveSearchView: builder.mutation<void, SaveSearchViewRequest>({
      query: body => ({ url: '/saved-views', method: 'PUT', body }),
      invalidatesTags: ['SavedView'],
      // Optimistically upsert into the cached list so the saved view appears
      // immediately (the PUT round-trip + refetch otherwise leaves a visible lag).
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          rysApi.util.updateQueryData('listSavedViews', undefined, draft => {
            const existing = draft.find(v => v.name === arg.name);
            if (existing) existing.state = arg.state;
            else draft.push({ name: arg.name, state: arg.state });
          }),
        );
        try { await queryFulfilled; } catch { patch.undo(); }
      },
    }),
    deleteSavedView: builder.mutation<void, string>({
      query: name => ({ url: `/saved-views/${encodeURIComponent(name)}`, method: 'DELETE' }),
      invalidatesTags: ['SavedView'],
      // Optimistically remove from the cached list for instant feedback.
      async onQueryStarted(name, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          rysApi.util.updateQueryData('listSavedViews', undefined, draft => {
            const i = draft.findIndex(v => v.name === name);
            if (i !== -1) draft.splice(i, 1);
          }),
        );
        try { await queryFulfilled; } catch { patch.undo(); }
      },
    }),

    // --- Audit log --------------------------------------------------------
    getAuditLog: builder.query<AuditLogItemDto[], void>({
      query: () => '/audit-log',
      providesTags: ['AuditLog'],
    }),

    // --- Config -----------------------------------------------------------
    getAuthConfig: builder.query<AuthConfig, void>({
      query: () => '/config/auth',
    }),

    getConstants: builder.query<ConstantsConfig, void>({
      query: () => '/config/constants',
    }),

    // --- Public upload ----------------------------------------------------
    submitPitchDeck: builder.mutation<
      SubmitResult,
      { tenantSlug: string; startupName: string; file: File; turnstileToken: string; details: PitchDeckSubmissionDetails }
    >({
      query: ({ tenantSlug, startupName, file, turnstileToken, details }) => {
        const form = new FormData();
        form.append('startupName', startupName);
        form.append('turnstileToken', turnstileToken);
        form.append('details', JSON.stringify(details));
        form.append('file', file);
        return { url: `/public/tenants/${tenantSlug}/pitchdecks`, method: 'POST', body: form };
      },
    }),

    // --- Admin / user management ----------------------------------------
    listUsers: builder.query<UserListItem[], void>({
      query: () => '/management/users',
      transformResponse: (raw: unknown) => validate(listUsers200Schema, raw) as UserListItem[],
      providesTags: ['User'],
    }),

    listInvites: builder.query<InviteListItem[], void>({
      query: () => '/management/invites',
      transformResponse: (raw: unknown) => validate(listInvites200Schema, raw) as InviteListItem[],
      providesTags: ['Invite'],
    }),

    inviteUser: builder.mutation<void, { email: string; role: UserRole }>({
      query: body => ({ url: '/management/invites', method: 'POST', body }),
      // Inviting creates/updates an inactive user too, so refresh both lists.
      invalidatesTags: ['Invite', 'User'],
    }),

    changeUserRole: builder.mutation<void, { userId: string; role: UserRole }>({
      query: ({ userId, role }) => ({ url: `/management/users/${userId}`, method: 'PATCH', body: { role } }),
      invalidatesTags: ['User'],
    }),

    deactivateUser: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({ url: `/management/users/${userId}/deactivate`, method: 'POST' }),
      invalidatesTags: ['User'],
    }),

    revokeInvite: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({ url: `/management/invites/${userId}`, method: 'DELETE' }),
      // Revoking removes the inactive user record too, so refresh both lists.
      invalidatesTags: ['Invite', 'User'],
    }),
  }),
});

/**
 * Warm a contiguous range of deck page images into the RTK Query cache
 * (inclusive `from`..`to`, 1-based) without holding subscriptions: each page is
 * fetched, then released so it stays cached for `keepUnusedDataFor` (60s) for
 * the lightbox's own subscriber to reuse. Best-effort and fire-and-forget — a
 * failed page simply isn't pre-cached. Re-requesting an already-cached page is a
 * no-op fetch. Used for the small upfront preload and the lightbox look-ahead.
 *
 * Pages are fetched SEQUENTIALLY in ascending page order (await each before the
 * next), so they finish in order — page 3 is cached before 4 starts — instead of
 * landing in whatever order a concurrent burst happens to return (the "1,2,5,6"
 * effect). This also bounds concurrency to one request, avoiding the
 * thundering-herd freeze of firing the whole range at once.
 */
export function prefetchDeckPages(dispatch: AppDispatch, deckId: string, from: number, to: number): void {
  const start = Math.max(1, from);
  void (async () => {
    for (let pageNumber = start; pageNumber <= to; pageNumber++) {
      const sub = dispatch(rysApi.endpoints.getPitchDeckPage.initiate({ deckId, pageNumber }));
      try {
        await sub.unwrap();
      } catch {
        // Best-effort: a failed page just isn't pre-cached.
      } finally {
        sub.unsubscribe();
      }
    }
  })();
}

export const {
  useListDealflowQuery,
  useVerifyClarificationMutation,
  useRejectClarificationMutation,
  useListPitchDecksQuery,
  useGetPitchDeckQuery,
  useGetPitchDeckPageQuery,
  useGetAttachmentBlobQuery,
  useLazyGetAttachmentBlobQuery,
  useSetReviewDecisionMutation,
  useSetWatchlistMutation,
  useSubmitRatingMutation,
  useRemoveRatingMutation,
  useListReviewersQuery,
  useRequestEmailCodeMutation,
  useVerifyEmailCodeMutation,
  useLogoutMutation,
  useGetMeQuery,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useUploadProfilePhotoMutation,
  useRemoveProfilePhotoMutation,
  useGetProfilePhotoQuery,
  useGetFundProfileQuery,
  useUpdateFundProfileMutation,
  useUploadFundLogoMutation,
  useRemoveFundLogoMutation,
  useGetFundLogoQuery,
  useGetFundThesisQuery,
  useUpdateFundThesisMutation,
  useGetFundThesisDraftQuery,
  useSaveFundThesisDraftMutation,
  useDiscardFundThesisDraftMutation,
  useGetAuditLogQuery,
  useGetAuthConfigQuery,
  useGetConstantsQuery,
  useSubmitPitchDeckMutation,
  useListUsersQuery,
  useListInvitesQuery,
  useInviteUserMutation,
  useChangeUserRoleMutation,
  useDeactivateUserMutation,
  useRevokeInviteMutation,
  useListSavedViewsQuery,
  useSaveSearchViewMutation,
  useDeleteSavedViewMutation,
} = rysApi;

export function startSsoUrl(provider: 'google' | 'microsoft', returnUrl: string): string {
  const params = new URLSearchParams({ return_url: returnUrl });
  return `${env.api.baseUrl}/auth/sso/${provider}/start?${params.toString()}`;
}
